import React, { useMemo, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import theme from './theme';
import './i18n';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import FacebookManager from './pages/FacebookManager';
import WhatsappManager from './pages/WhatsappManager';
import TelegramManager from './pages/TelegramManager';
import InstagramManager from './pages/InstagramManager';
import AccountsManager from './pages/AccountsManager';
import Campaigns from './pages/Campaigns';
import Chatbot from './pages/Chatbot';
import BillingDashboard from './pages/BillingDashboard';
import AdminDashboard from './pages/AdminDashboard';
import PaymentRequests from './pages/PaymentRequests';
import DraftsManager from './pages/DraftsManager';
import ContentCalendar from './pages/ContentCalendar';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import TemplatesManager from './pages/TemplatesManager';
import ActivityLogs from './pages/ActivityLogs';
import ScheduledPosts from './pages/ScheduledPosts';
import DraftEditor from './pages/DraftEditor';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import { loadUser } from './redux/authSlice';

const useAuth = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

const PrivateRoute = ({ children }) => {
  const isAuth = useAuth();
  return isAuth ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const isAuth = useAuth();
  return isAuth ? <Navigate to="/dashboard" /> : children;
};

function App() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    // Load user if token exists
    if (localStorage.getItem('token')) {
      dispatch(loadUser());
    }

    // Set initial direction based on language
    document.dir = i18n.dir();
    document.documentElement.lang = i18n.language;

    // Listen for language changes
    const handleLanguageChanged = (lng) => {
      document.dir = i18n.dir(lng);
      document.documentElement.lang = lng;
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/pricing" element={<PublicRoute><PricingPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><LoginPage /></PublicRoute>} />

        {/* Private Routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/scheduled"
          element={
            <PrivateRoute>
              <Layout>
                <ScheduledPosts />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/drafts/new"
          element={
            <PrivateRoute>
              <Layout>
                <DraftEditor />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/drafts/edit/:id"
          element={
            <PrivateRoute>
              <Layout>
                <DraftEditor />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/facebook"
          element={
            <PrivateRoute>
              <Layout>
                <FacebookManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/whatsapp"
          element={
            <PrivateRoute>
              <Layout>
                <WhatsappManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/telegram"
          element={
            <PrivateRoute>
              <Layout>
                <TelegramManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/instagram"
          element={
            <PrivateRoute>
              <Layout>
                <InstagramManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/campaigns"
          element={
            <PrivateRoute>
              <Layout>
                <Campaigns />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/chatbot"
          element={
            <PrivateRoute>
              <Layout>
                <Chatbot />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <PrivateRoute>
              <Layout>
                <AccountsManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <PrivateRoute>
              <Layout>
                <BillingDashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <Layout>
                <AdminDashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <PrivateRoute>
              <Layout>
                <PaymentRequests />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/drafts"
          element={
            <PrivateRoute>
              <Layout>
                <DraftsManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <PrivateRoute>
              <Layout>
                <ContentCalendar />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <PrivateRoute>
              <Layout>
                <AnalyticsDashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <PrivateRoute>
              <Layout>
                <TemplatesManager />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/activity-logs"
          element={
            <PrivateRoute>
              <Layout>
                <ActivityLogs />
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
